/**
 * List of SS APIs.
 * Note: remember to add them to ErrorHandlerInterceptor if applicable.
 */
export const SS_CHECKOUT_ENDPOINT = "api/checkout";
export const SS_PRINT_ENDPOINT = "api/print";
export const SS_POS_ACTION_ENDPOINT = "api/posaction";
/**
 * API to retrieve fidelity data from a SS pos.
 */
export const SS_FIDELITY_EVALUATION_ENDPOINT = "api/fidelity";
/**
 * API to validate coupons from a SS pos.
 */
export const SS_COUPONS_VALIDATE_ENDPOINT = "coupons/validate";
/**
 * API to trigger a 'coupon release' request from a SS pos. SS will actually call SSC APIs to release the coupon.
 */
export const SS_COUPONS_RELEASE_ENDPOINT = "coupons/release";


/**
 * Result type returned by StockStore apis
 */
export interface SsResult<T> {
  statusCode: string;
  errorMessages: string[];
  isSuccess: boolean;
  result: T
}

/**
 * StatusCode enum used by SsResult
 */
export abstract class SsResultStatusCode {
  /**
   * Error value not actually returned. Used for client side errors (network related, generic, etc.)
   */
  static readonly ERR_CLIENT = "ERR_CLIENT";
  /**
   * Returned in case of generic error between SS and SSC.
   * Returned by: coupon API
   */
  static readonly ERR_CLOUD_KO = "ERR_CLOUD_KO";
  /**
   * Returned in case of success
   * Returned by every SS API
   */
  static readonly OK = "OK";
  /**
   Returned in case of success, but with the end of paper flag.
   Returned by checkout API
   */
  static readonly OK_EOP = "OK_EOP";
  /**
   * Returned in case of TM error
   * Returned by checkout API
   */
  static readonly ERR_PRINTER_KO = "ERR_PRINTER_KO";
  /**
   * Returned in case of transaction abort by the customer
   */
  static readonly ERR_ABORT = "ERR_ABORT";
  /**
   * Returned in case of generic payload error
   * Returned by every ss API
   */
  static readonly ERR_PAYLOAD = "ERR_PAYLOAD";
  /**
   * Returned in case of POS error
   * Returned by checkout API
   */
  static readonly ERR_POS_KO = "ERR_POS_KO";
  /**
   * Returned in case of POS timeout
   * Returned by checkout API
   */
  static readonly ERR_POS_TIMEOUT = "ERR_POS_TIMEOUT";
  /**
   * Used in case of SS timeout. Generic, applies to multiple APIs
   */
  static readonly ERR_SS_TIMEOUT = "ERR_SS_TIMEOUT";
  /**
   * Used in case of cart error during calculate API. This could happen when there is inconsistency between SS and Kiosk.
   */
  static readonly ERR_SS_CART_INVALID = "ERR_SS_CART_INVALID";
  /**
   * Returned by fidelity APIs where the specified card code is not found by SS
   */
  static readonly ERR_FIDELITY_NOT_FOUND = "ERR_FIDELITY_NOT_FOUND";
  /**
   * Returned by coupons APIs where the specified coupon code is not found by SS
   */
  static readonly ERR_COUPON_NOT_FOUND = "ERR_COUPON_NOT_FOUND";
  /**
   * Returned by coupons APIs where the specified coupon is not valid for a generic multitude of reasons (validity check fails)
   * Use the other specific
   */
  static readonly ERR_COUPON_NOT_VALID = "ERR_COUPON_NOT_VALID";
  /**
   * Returned by coupons APIs where the specified coupon is already in use in another order
   */
  static readonly ERR_COUPON_IN_USE = "ERR_COUPON_IN_USE";
  /**
   * Returned by coupons APIs when the coupon is valid, can be used, and is associated to a promo article that the customer should add to the cart
   */
  static readonly OK_COUPON_ARTICLE_PROMO = "OK_COUPON_ARTICLE_PROMO";
  /**
   * Returned by coupons APIs when the coupon has reached its maximum usage limit
   */
  static readonly ERR_COUPON_MAX_USAGE_LIMIT_REACHED = "ERR_COUPON_MAX_USAGE_LIMIT_REACHED";
  /**
   * Returned by coupons APIs when the coupon has expired and is no longer valid
   */
  static readonly ERR_COUPON_EXPIRED = "ERR_COUPON_EXPIRED";
  /**
   * Returned by coupons APIs when the coupon has time restrictions and is not active at the current time
   */
  static readonly ERR_COUPON_TIME_RESTRICTIONS = "ERR_COUPON_TIME_RESTRICTIONS";
  /**
   * Used for client-side validation when coupon is already in cart
   */
  static readonly ERR_COUPON_ALREADY_IN_CART = "ERR_COUPON_ALREADY_IN_CART";

}


/**
 * Body used by the 'posaction' api
 */
export interface PosActionApiBody {
  action: "show" | "hide" | "quit_kiosk" | "quit";
}

/**
 * Type returned by SS to indicate a promotional article, such as the one related to an inserted coupon.
 */
export interface SsPromotionalArticleData {
  /**
   * Code of the article. Kiosk API will fetch related article data using this code.
   */
  articleCode: string;
  /**
   * Price override in INTEGER (100 equals 1 euro). If specified, any other price calculation will be discarded.
   */
  priceOverride?: number;
  /**
   * Code of the promotion that associated this promotional article to the coupon.
   */
  promoCode?: string;
}
